import { ChevronDown, ChevronRight } from 'lucide-react';
import Badge from '../../ui/Badge';
import { formatMoney, formatDate } from '../../../utils/formatters';
import { qboDocLabel } from './MatchRow';
import {
  STATUS_MATCH,
  statusLabel,
  statusBadgeTone,
  statusIcon,
  formatDelta,
} from './poAuditHelpers';

// One vendor reconciliation row inside a PO accordion.
//
// Layout:
//   ┌──────────────────────────────────────────────────────────────────┐
//   │ [icon] Vendor name    $qbo / $tek   ±$delta   [▼]                │
//   └──────────────────────────────────────────────────────────────────┘
//   ... expanded body: QBO bills (left) | Tek pops (right)
//
// "Match" rows are de-emphasized (opacity-60) so the eye focuses on the
// red/amber/purple/gray issue rows.

export default function VendorRecoRow({ vendor, expanded, onToggleExpand }) {
  const isMatch = vendor.status === STATUS_MATCH;
  const Icon = statusIcon(vendor.status);
  const tone = statusBadgeTone(vendor.status);

  const qbo = vendor.qbo_total_cents || 0;
  const tek = vendor.tek_loaded_cents || 0;
  const delta = vendor.delta_cents ?? (qbo - tek);

  // Whether the body has anything worth expanding to.
  const hasBody =
    (vendor.qbo_bills?.length || 0) > 0 ||
    (vendor.tek_pops?.length || 0) > 0;

  // Tone-aware text color for the status icon.
  const iconColor = ({
    recovery: 'text-[var(--color-recovery-600)]',
    danger:   'text-[var(--color-danger-600)]',
    action:   'text-[var(--color-action-700)]',
    insight:  'text-[var(--color-insight-600)]',
    cream:    'text-[var(--color-warm-500)]',
  }[tone]) || 'text-[var(--color-warm-500)]';

  // Tone-aware text color for the delta.
  const deltaColor = (() => {
    if (!delta) return 'text-[var(--color-warm-500)]';
    if (vendor.status === STATUS_MATCH) return 'text-[var(--color-warm-500)]';
    if (tone === 'danger')  return 'text-[var(--color-danger-700)]';
    if (tone === 'action')  return 'text-[var(--color-action-700)]';
    if (tone === 'insight') return 'text-[var(--color-insight-700)]';
    return 'text-[var(--color-warm-700)]';
  })();

  return (
    <div
      className={`
        border-b border-[var(--color-warm-100)] last:border-b-0
        ${isMatch ? 'bg-[var(--color-cream-50)]/60' : 'bg-[var(--color-cream-50)]'}
        ${isMatch && !expanded ? 'opacity-60' : ''}
      `}
    >
      {/* Header row — clickable to expand */}
      <button
        type="button"
        onClick={() => hasBody && onToggleExpand?.()}
        disabled={!hasBody}
        className={`
          w-full flex items-center gap-3 px-5 py-2.5 text-left
          ${hasBody ? 'hover:bg-[var(--color-cream-100)]/70 cursor-pointer' : 'cursor-default'}
          transition-colors
        `}
      >
        <Icon size={15} className={`shrink-0 ${iconColor}`} />

        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-[var(--color-warm-900)] truncate">
            {vendor.vendor_display || vendor.vendor_canonical || '— vendor —'}
          </div>
          <div className="text-[11px] text-[var(--color-warm-500)] mt-0.5 flex items-center gap-1.5">
            <Badge tone={tone} size="xs">{statusLabel(vendor.status)}</Badge>
            <span className="text-[var(--color-warm-300)]">·</span>
            <span>
              {vendor.qbo_bills?.length || 0} QBO bill{(vendor.qbo_bills?.length || 0) === 1 ? '' : 's'}
            </span>
            <span className="text-[var(--color-warm-300)]">·</span>
            <span>
              {vendor.tek_pops?.length || 0} Tek pop{(vendor.tek_pops?.length || 0) === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        <div className="shrink-0 text-right">
          <div className="text-[12px] tabular-nums font-semibold text-[var(--color-warm-900)]">
            {formatMoney(qbo / 100)}
            <span className="text-[var(--color-warm-400)] font-normal mx-1">/</span>
            {formatMoney(tek / 100)}
          </div>
          <div className={`text-[11px] tabular-nums font-semibold mt-0.5 ${deltaColor}`}>
            Δ {formatDelta(delta)}
          </div>
        </div>

        {hasBody && (
          <div className="shrink-0 ml-1 text-[var(--color-warm-500)]">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </div>
        )}
      </button>

      {/* Expanded body — QBO bills + Tek pops side-by-side */}
      {expanded && hasBody && (
        <div className="px-5 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-[var(--color-warm-100)]/60">
          {/* QBO bills */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)] mb-2">
              QBO Bills · {vendor.qbo_bills?.length || 0}
            </div>
            {(!vendor.qbo_bills?.length) && (
              <div className="text-[11px] italic text-[var(--color-warm-400)]">
                No QBO bills for this vendor on this PO.
              </div>
            )}
            <ul className="space-y-1.5">
              {vendor.qbo_bills?.map((b) => (
                <li
                  key={b.match_id || `${b.txn_id}-${b.doc_number || ''}`}
                  className="text-[12px] bg-[var(--color-cream-100)]/70 border border-[var(--color-warm-100)] rounded-[var(--radius-md)] px-2.5 py-1.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 truncate">
                      {b.doc_number && (
                        <span className="mono-nums font-semibold text-[var(--color-warm-800)]">
                          {qboDocLabel(b.txn_type)}{b.doc_number}
                        </span>
                      )}
                      {b.txn_id && (
                        <span className="text-[11px] text-[var(--color-warm-500)] mono-nums ml-1.5">
                          · txn {String(b.txn_id).slice(-8)}
                        </span>
                      )}
                      <span className="text-[11px] text-[var(--color-warm-500)] ml-1.5">
                        · {formatDate(b.date)}
                      </span>
                    </div>
                    <div className="shrink-0 tabular-nums font-semibold text-[var(--color-warm-900)]">
                      {formatMoney((b.amount_cents || 0) / 100)}
                    </div>
                  </div>
                  {b.memo && (
                    <div className="text-[11px] text-[var(--color-warm-600)] italic mt-1 line-clamp-2">
                      {b.memo}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Tek pops */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)] mb-2">
              Tek Pops · {vendor.tek_pops?.length || 0}
            </div>
            {(!vendor.tek_pops?.length) && (
              <div className="text-[11px] italic text-[var(--color-warm-400)]">
                No Tekmetric pops for this vendor on this PO.
              </div>
            )}
            <ul className="space-y-1.5">
              {vendor.tek_pops?.map((p) => {
                const loaded = p.pop_used_loaded_cents
                  ?? ((p.cost_each_cents || 0) * (p.received || 0));
                return (
                  <li
                    key={p.pop_id}
                    className="text-[12px] bg-[var(--color-cream-100)]/70 border border-[var(--color-warm-100)] rounded-[var(--radius-md)] px-2.5 py-1.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 truncate">
                        {p.ro_number && (
                          <span className="mono-nums font-semibold text-[var(--color-warm-800)]">
                            RO #{p.ro_number}
                          </span>
                        )}
                        {p.pop_id && (
                          <span className="text-[11px] text-[var(--color-warm-500)] mono-nums ml-1.5">
                            · pop {String(p.pop_id).slice(-6)}
                          </span>
                        )}
                        {p.part_name && (
                          <span className="text-[11px] text-[var(--color-warm-600)] ml-1.5 truncate">
                            · {p.part_name}
                          </span>
                        )}
                      </div>
                      <div className="shrink-0 tabular-nums font-semibold text-[var(--color-warm-900)]">
                        {formatMoney(loaded / 100)}
                      </div>
                    </div>
                    <div className="text-[11px] text-[var(--color-warm-500)] mt-1 flex items-center gap-1.5 flex-wrap">
                      <span className="tabular-nums">
                        {p.received ?? 0} × {formatMoney((p.cost_each_cents || 0) / 100)}
                      </span>
                      {p.order_date && (
                        <>
                          <span className="text-[var(--color-warm-300)]">·</span>
                          <span>{formatDate(p.order_date)}</span>
                        </>
                      )}
                      {p.invoice_number && (
                        <>
                          <span className="text-[var(--color-warm-300)]">·</span>
                          <span className="mono-nums">inv {p.invoice_number}</span>
                        </>
                      )}
                      {p.warranty && (
                        <>
                          <span className="text-[var(--color-warm-300)]">·</span>
                          <Badge tone="insight" size="xs">warranty</Badge>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
